import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
	IChart,
	ICompareSettings,
	ICorrelateSettings,
	IKpiSourceData,
} from '@interfaces/kpi/kpiCommon';

import { EAggregationTypes, TAggregationTypes } from '@enums/kpi-api';

const MAX_CHARTS_TO_COMPARE = 10;
const MAX_CHARTS_TO_CORRELATE = 2;
export const MIN_POINTS_IN_CHART = 3;

interface IInitialState {
	activeCharts: Array<IChart>;
	siteIdsOfActiveCharts: Array<string>;
	aggregationTypeOnMap: TAggregationTypes | null;
	compareCharts: Array<IChart>;
	compareSettings: ICompareSettings;
	correlateCharts: Array<IChart>;
	correlateSettings: ICorrelateSettings;
	lastKpiSourceData: IKpiSourceData | null;
	isKpiSourceClicked: boolean;
	chartsReceivedAt: number | null;
}

const initialState: IInitialState = {
	activeCharts: [],
	siteIdsOfActiveCharts: [],
	aggregationTypeOnMap: null,
	compareCharts: [],
	correlateCharts: [],
	compareSettings: {
		aggregationTypes: [EAggregationTypes.aggregated, EAggregationTypes.siteLevel],
		chartType: null,
		chartsCount: 0,
		timeAggregationType: null,
		timeCount: 0,
	},
	correlateSettings: {
		chartsCount: 0,
		timeCount: 0,
		aggregationTypes: [EAggregationTypes.aggregated, EAggregationTypes.siteLevel],
	},
	lastKpiSourceData: null,
	isKpiSourceClicked: false,
	chartsReceivedAt: null,
};

const common = createSlice({
	name: 'kpi',
	initialState,
	reducers: {
		setCompareCharts: (state, action: PayloadAction<{
			chart: IChart;
		}>) => {
			const chart = action.payload.chart;
			const compareSettings = state.compareSettings;

			const isExistChart = state.compareCharts.map(currentChart =>
				currentChart.chartId).includes(chart.chartId);

			const aggregationType = chart.aggregationType.name;
			const timeCount = chart.lines[0].points.length;
			const timeAggregationType = chart.yaxisAggregationType.name;

			const isPassFirstChecksToCompare = !state.compareCharts.length
				&& compareSettings.aggregationTypes.includes(aggregationType);

			const isPassChecksToCompare =
				compareSettings.aggregationTypes.includes(aggregationType)
				&& compareSettings.chartsCount < MAX_CHARTS_TO_COMPARE
				&& compareSettings.timeCount === timeCount
				&& compareSettings.timeAggregationType === timeAggregationType;

			if (isExistChart) {
				if (state.compareCharts.length === 1) {
					state.compareCharts = [];
					state.compareSettings = initialState.compareSettings;
				} else {
					state.compareCharts =
						state.compareCharts.filter(currentChart => currentChart.chartId !== chart.chartId);
					state.compareSettings = {
						...compareSettings,
						chartsCount: --compareSettings.chartsCount,
					};
				}
			} else if (isPassFirstChecksToCompare || isPassChecksToCompare) {
				state.compareCharts.push(chart);
				state.compareSettings = {
					...compareSettings,
					timeAggregationType,
					timeCount,
					chartsCount: ++compareSettings.chartsCount,
				};
			}
		},
		setCorrelateCharts: (state, action: PayloadAction<{
			chart: IChart;
		}>) => {
			const chart = action.payload.chart;
			const correlateSettings = state.correlateSettings;

			const isExistChart = state.correlateCharts.map(currentChart =>
				currentChart.chartId).includes(chart.chartId);

			const aggregationType = chart.aggregationType.name;
			const availableValues = chart.lines[0].points.filter(point => point.y !== null);
			const timeCount = availableValues.length;

			const isPassFirstChecksToCorrelate = !state.correlateCharts.length
				&& correlateSettings.aggregationTypes.includes(aggregationType)
				&& !availableValues.every(value => value.y === availableValues[0].y)
				&& availableValues.length >= MIN_POINTS_IN_CHART;

			const isPassChecksToCorrelate = correlateSettings.aggregationTypes.includes(aggregationType)
				&& correlateSettings.chartsCount < MAX_CHARTS_TO_CORRELATE
				&& correlateSettings.timeCount === timeCount
				&& !availableValues.every(value => value.y === availableValues[0].y)
				&& availableValues.length >= MIN_POINTS_IN_CHART;

			if (isExistChart) {
				if (state.correlateCharts.length === 1) {
					state.correlateCharts = [];
					state.correlateSettings = initialState.correlateSettings;
				} else {
					state.correlateCharts =
						state.correlateCharts.filter(currentChart => currentChart.chartId !== chart.chartId);
					state.correlateSettings = {
						...correlateSettings,
						chartsCount: --correlateSettings.chartsCount,
					};
				}
			} else if (isPassFirstChecksToCorrelate || isPassChecksToCorrelate) {
				state.correlateCharts.push(chart);
				state.correlateSettings = {
					...correlateSettings,
					timeCount,
					chartsCount: ++correlateSettings.chartsCount,
				};
			}
		},
		clearCompareCharts: (state) => {
			state.compareCharts = [];
			state.compareSettings = initialState.compareSettings;
		},
		clearCorrelateCharts: (state) => {
			state.correlateCharts = [];
			state.correlateSettings = initialState.correlateSettings;
		},
		setActiveCharts: (state, action: PayloadAction<Array<IChart>>) => {
			state.activeCharts = action.payload;
		},
		clearActiveCharts: (state) => {
			state.activeCharts = [];
		},
		setAggregationTypeOnMap: (state, action: PayloadAction<TAggregationTypes | null>) => {
			state.aggregationTypeOnMap = action.payload;
		},
		setSiteIdsOfActiveCharts: (state, action: PayloadAction<Array<string>>) => {
			state.siteIdsOfActiveCharts = action.payload;
		},
		setLastKpiSourceData: (state, action: PayloadAction<IKpiSourceData | null>) => {
			state.lastKpiSourceData = action.payload;
		},
		setKpiSourceClickedState: (state, action: PayloadAction<boolean>) => {
			state.isKpiSourceClicked = action.payload;
		},
		setChartsReceivedAt: (state, action: PayloadAction<number | null>) => {
			state.chartsReceivedAt = action.payload;
		},
	},
});

export const {
	clearActiveCharts,
	clearCompareCharts,
	clearCorrelateCharts,
	setActiveCharts,
	setCompareCharts,
	setCorrelateCharts,
	setAggregationTypeOnMap,
	setSiteIdsOfActiveCharts,
	setLastKpiSourceData,
	setKpiSourceClickedState,
	setChartsReceivedAt,
} = common.actions;

export default common.reducer;
